// src/components/items/FormAccardion.jsx
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import AccordionArrow from '../../icons/AccordionArrow';

export default function FormAccardion({ number, title, child, content, isOpen: isOpenProp, disabled = false }) {
  const [isOpen, setIsOpen] = useState(isOpenProp ?? false);
  const [contentHeight, setContentHeight] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const innerRef = useRef(null);
  const forcedOpen = isOpenProp === true;

  useEffect(() => {
    if (isOpenProp === undefined) return;
    setIsOpen(isOpenProp);
  }, [isOpenProp]);

  const toggleOpen = () => {
    if (disabled || forcedOpen) return;
    setIsOpen(prev => !prev);
  };

  const measure = () => {
    const el = innerRef.current;
    if (!el) return;
    setContentHeight(el.scrollHeight);
  };

  useLayoutEffect(() => {
    measure();
  }, [isOpen, content]);

  useEffect(() => {
    if (!isOpen) return;
    const timers = [50, 150, 350, 700].map((ms) => setTimeout(measure, ms));
    return () => timers.forEach(clearTimeout);
  }, [isOpen, content]);

  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (isOpen) setContentHeight(el.scrollHeight);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]);

  // Forced open: skip clipped animation height, expand fully at once
  const animatedHeight = isOpen ? (forcedOpen ? 'auto' : contentHeight) : 0;
  const styleHeight = forcedOpen && isOpen
    ? 'auto'
    : (isOpen && !isAnimating ? 'auto' : animatedHeight);

  return (
    <div className={`w-full border border-white/20 rounded-[8px] ${forcedOpen ? 'overflow-visible' : 'overflow-hidden'} ${disabled ? 'opacity-50' : ''}`}>
      <div
        className={`w-full h-[66px] px-[16px] py-[14px] flex flex-col justify-center ${disabled || forcedOpen ? 'cursor-default' : 'cursor-pointer'}`}
        onClick={toggleOpen}
      >
        <div className="w-full h-[26px] flex flex-row items-center justify-between">
          <div className="flex flex-row gap-[10px] items-center">
            <div className="w-[24px] h-[24px] flex flex-row flex-shrink-0 items-center justify-center font-roboto bg-transparent border border-white rounded-full text-[13px] text-white">
              {number}
            </div>
            <span className="text-[13px] text-white w-[108px] whitespace-nowrap">
              {title}
            </span>
          </div>

          <div className="flex flex-row w-full items-center justify-end gap-[10px]">
            {child}
            {content && (
              <AccordionArrow
                className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
              />
            )}
          </div>
        </div>
      </div>

      <motion.div
        className={forcedOpen ? 'overflow-visible' : 'overflow-hidden'}
        style={{ height: styleHeight }}
        animate={forcedOpen
          ? { height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }
          : { height: animatedHeight, opacity: isOpen ? 1 : 0 }
        }
        initial={false}
        transition={{ duration: forcedOpen ? 0 : 0.25, ease: 'linear' }}
        onUpdate={() => {
          if (!forcedOpen && !isAnimating) setIsAnimating(true);
        }}
        onAnimationComplete={() => {
          setIsAnimating(false);
          if (isOpen) measure();
        }}
      >
        <div ref={innerRef} className=" text-white text-[13px]">
          {content}
        </div>
      </motion.div>
    </div>
  );
}
